/**
 * Doppelseite im Querformat: zwei Seiten nebeneinander, gleich hoch und so groß,
 * dass beide in den Rahmen passen. Geblättert wird um je eine Seite – die rechte
 * Seite rückt nach links, wie beim Notenheft auf dem Pult.
 */

/** Kein Abstand: die beiden Seiten liegen wie ein aufgeschlagenes Heft aneinander. */
export const SPREAD_GAP = 0;

export interface SpreadSize {
  /** Gemeinsame Höhe der Seiten (CSS-Pixel) */
  height: number;
  /** Breite je Seite (CSS-Pixel) */
  widths: number[];
}

/** Passt Seiten (Seitenverhältnis Höhe/Breite) nebeneinander in einen Rahmen ein. */
export function fitSpread(
  aspects: readonly number[],
  frameWidth: number,
  frameHeight: number,
  gap = SPREAD_GAP,
): SpreadSize {
  const valid = aspects.filter((a) => Number.isFinite(a) && a > 0);
  if (!valid.length || frameWidth <= 0 || frameHeight <= 0) return { height: 0, widths: [] };
  const inverse = valid.reduce((sum, a) => sum + 1 / a, 0);
  const byWidth = Math.max(0, frameWidth - gap * (valid.length - 1)) / inverse;
  const height = Math.max(1, Math.min(frameHeight, byWidth));
  return { height, widths: valid.map((a) => height / a) };
}

/** Die letzte Doppelseite zeigt die beiden letzten Seiten. */
export function lastSpreadStart(pageCount: number): number {
  return Math.max(1, pageCount - 1);
}

export function clampSpreadStart(start: number, pageCount: number): number {
  if (!Number.isFinite(start)) return 1;
  return Math.min(lastSpreadStart(pageCount), Math.max(1, Math.round(start)));
}

/** Seitennummern der Doppelseite ab `start` (bei einseitigen Dokumenten nur eine). */
export function spreadPages(start: number, pageCount: number): number[] {
  if (pageCount < 1) return [];
  const first = clampSpreadStart(start, pageCount);
  return first + 1 <= pageCount ? [first, first + 1] : [first];
}

/** Um eine Seite weiterblättern; `null`, wenn es in diese Richtung nicht weitergeht. */
export function turnSpread(start: number, direction: 1 | -1, pageCount: number): number | null {
  const current = clampSpreadStart(start, pageCount);
  const next = clampSpreadStart(current + direction, pageCount);
  return next === current ? null : next;
}

/** Startseite, auf der `page` zu sehen ist – bleibt stehen, wenn sie schon sichtbar ist. */
export function spreadStartFor(page: number, current: number, pageCount: number): number {
  const visible = spreadPages(current, pageCount);
  return visible.includes(page) ? clampSpreadStart(current, pageCount) : clampSpreadStart(page, pageCount);
}

/**
 * Seiten, die vorab gerendert bereitliegen sollen: die sichtbare Doppelseite, ein paar
 * Seiten davor und dahinter sowie die Doppelseiten an Sprungzielen. So ist beim Blättern
 * und Springen alles schon gezeichnet. Bewusst begrenzt – iPad-Safari hat wenig Canvas-Speicher.
 */
export function spreadWindow(
  start: number,
  pageCount: number,
  targets: readonly number[] = [],
  behind = 2,
  ahead = 3,
): number[] {
  if (pageCount < 1) return [];
  const first = clampSpreadStart(start, pageCount);
  const pages = new Set<number>();
  for (let n = first - behind; n <= first + 1 + ahead; n++) {
    if (n >= 1 && n <= pageCount) pages.add(n);
  }
  for (const target of targets) {
    for (const n of spreadPages(spreadStartFor(target, first, pageCount), pageCount)) pages.add(n);
  }
  return [...pages].sort((a, b) => a - b);
}
